import { ft } from '../core/units.js';
import { SEAT_RADIUS_FEET } from '../data/objects.js';
import { definition } from './mass.js';

/**
 * What your hands can do where you are standing.
 *
 * One function, called every frame, that turns (position, what you are
 * carrying, what is already in the room) into a single offered action. The
 * player never chooses between verbs — there is one E and one Q and the game
 * works out what they mean here — which is the only way a setup stays fast
 * enough to beat a deadline.
 *
 * It does not know what a round, a chair or a linen is. It reads `place` off
 * the object definition: what the thing wants underneath it (`onto`) and which
 * slot it fills (`role`). Three roles exist, because three is what a banquet
 * room needs — something stands on the mark, things ring it, one thing covers
 * it — and a fourth is a line of data away. Adding an object that goes on a
 * table takes no code at all.
 *
 * The reach is five foot ten against a twelve foot grid: deliberately just
 * under half the spacing, so no point in the room is inside two anchors at once
 * and the snap is never a coin toss between neighbours. It still comfortably
 * clears the chair ring at four and a half feet, so you can set a chair while
 * standing behind the ones already down.
 */

export const REACH = ft(5.9);

/** Roles, in the order a room is struck: last on, first off. */
export const STRIKE_ORDER = ['seat', 'cover', 'base'];

export function resolvePlacement({ setup, carry, position, spec, tolerance = 1 }) {
  const item = carry.kind ? definition(carry.kind) : null;
  const rule = item?.place;
  if (!rule) return { action: null, why: null };

  const reach = REACH * tolerance;

  if (rule.onto === 'anchor') {
    const free = setup.nearest(position, { within: reach, occupied: false });
    if (!free) {
      const taken = setup.nearest(position, { within: reach, occupied: true });
      return taken
        ? { action: null, why: `There is already a ${item.name.toLowerCase()} here.` }
        : { action: null, why: 'Stand on a setting mark.' };
    }
    return {
      action: 'place',
      role: rule.role,
      anchor: free.anchor,
      label: `Set the ${item.name.toLowerCase()}`,
      bad: free.anchor.keepClear,
    };
  }

  // Everything else goes onto whatever is already standing on a mark.
  const near = setup.nearest(position, { within: reach, occupied: true });
  if (!near) return { action: null, why: `${item.plural ?? item.name} go on a round.` };
  const table = setup.tableAt(near.anchor.key);

  if (rule.role === 'cover') {
    if (table.cover) return { action: null, why: 'That one is dressed.' };
    return {
      action: 'place',
      role: rule.role,
      anchor: near.anchor,
      table,
      variant: carry.variant,
      label: `Lay the ${carry.variant ?? item.name.toLowerCase()}`,
      // Nothing stops you dressing a room in the wrong colour. The BEO will.
      bad: Boolean(spec) && carry.variant !== spec.linen,
    };
  }

  const max = rule.max ?? Infinity;
  if (table.seats >= max) {
    return { action: null, why: `That round will not take another.` };
  }
  const wanted = spec?.chairsPerTable ?? max;
  return {
    action: 'place',
    role: rule.role,
    anchor: near.anchor,
    table,
    label: `Set a ${item.name.toLowerCase()}  (${table.seats}/${wanted})`,
    bad: table.seats >= wanted,
  };
}

/**
 * What Q would strike, and what it gives you back.
 *
 * Available with empty hands or full ones, because the moment you need it is
 * the moment you have just realised the whole block is one anchor off with six
 * chairs in your arms.
 *
 * Strikes in role order, which is the order a room is actually struck, so
 * pulling a misplaced round cannot silently bin the eight chairs on it.
 */
/*
 * `linen` is the colour the sheet wants. A cloth of another colour comes off
 * first, chairs or not: the strike used to work down from the top, so fixing
 * one wrong cloth on a seated round of ten was ten chairs off and ten back on.
 */
export function resolveStrike({ setup, position, linen = null }) {
  const near = setup.nearest(position, { within: REACH, occupied: true });
  if (!near) return { action: null };
  const table = setup.tableAt(near.anchor.key);
  const wrongCloth = Boolean(linen && table.cover && table.cover.variant !== linen);
  const role = wrongCloth ? 'cover' : table.seats > 0 ? 'seat' : table.cover ? 'cover' : 'base';
  const kind = role === 'seat' ? table.seatKind : role === 'cover' ? table.cover.kind : table.kind;
  const name = definition(kind)?.name.toLowerCase() ?? 'it';
  return {
    action: 'strike',
    anchor: near.anchor,
    table,
    role,
    removes: kind,
    label: role === 'seat' ? `Pull a ${name}` : role === 'cover' ? `Strip the ${name}` : `Strike the ${name}`,
  };
}

/** Metres from a table's centre to its chairs. */
export const SEAT_RADIUS = ft(SEAT_RADIUS_FEET);
